import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DsButton from '../../DsButton';
import DsModal from '../../DsModal';
import DsSwitch from '../../DsSwitch';
import ChatImageModal from '../composer/ChatImageModal';
import EventNoteMaterialPicker from './EventNoteMaterialPicker';
import { SkeletonAvatar, SkeletonButton, SkeletonLine, SkeletonMedia } from '../../Skeleton';
import { formatDateTimeJST } from '../../../../utils/datetime';
import { appendVariant, buildChatAssetUrl } from '../composer/chatAssetUtils';
import { useConfirm } from '../../../../design-system/ConfirmDialog';
import { useToast } from '../../ToastSystem';
import { usePdfPreview } from '../../pdf/PdfPreviewProvider';
import { downloadBlobFromUrl } from '../../../../api/storage';
import { createStorageProxyUrl, uploadMaterialThroughApi } from '../../../../api/venues';
import {
  formatBytes,
  getFileIcon,
  isAllowedFile,
  openOrSaveEventMaterial,
} from '../../events/eventMaterialExplorerUtils';
import {
  createChatNoteByUrl,
  deleteChatNoteByUrl,
  getChatNoteDetailByUrl,
  listChatNotesByUrl,
  updateChatNoteByUrl,
  uploadNoteImageThroughApi,
} from '../../../../api/chatNotes';
import { linkifyTextNodes } from '../timeline/linkifyTextNodes';
import {
  CHAT_IMAGE_ACCEPT_ATTR,
  isChatImageFile,
  isHeifChatImageFile,
  normalizeChatImagesForUpload,
} from '../composer/chatImageCompatibility';
import '../../../../css/common/chat/ChatNotesAttachments.css';

const API_URL = process.env.REACT_APP_API_URL;
const ADMIN_PROFILE_IMAGE_PATH = process.env.REACT_APP_ADMIN_PROFILE_IMAGE_PATH;
const ARTIST_PROFILE_IMAGE_PATH = process.env.REACT_APP_ARTIST_PROFILE_IMAGE_PATH;
const MAX_IMAGES = 20;
const NOTE_IMAGE_MAX_BYTES = 50 * 1024 * 1024;
const NOTE_IMAGE_MAX_MB = 50;
const MAX_EVENT_ATTACHMENTS = 50;

const isImageAttachment = (attachment) => attachment?.display_kind === 'image' ||
  /^image\//i.test(String(attachment?.content_type || attachment?.file?.type || '')) ||
  /\.(?:jpe?g|png|gif|webp|heic|heif)$/i.test(String(attachment?.file_name || attachment?.file?.name || ''));

const getNoteErrorMessage = (error, fallback) => {
  const message = typeof error?.message === 'string' ? error.message.trim() : '';
  return message || fallback;
};

const renderContentWithLinks = (text) => {
  if (!text) return null;
  return linkifyTextNodes(text).map((node) =>
    React.isValidElement(node) && node.type === 'a'
      ? React.cloneElement(node, { onClick: (event) => event.stopPropagation() })
      : node
  );
};

const buildProfileImageUrl = (note) => {
  const file = String(note?.author_profile_picture || '').trim();
  if (!file) return '';
  const root = note?.author_type === 'admin' ? ADMIN_PROFILE_IMAGE_PATH : ARTIST_PROFILE_IMAGE_PATH;
  return appendVariant(buildChatAssetUrl({ apiUrl: API_URL, root, file }), 'proxy');
};

const getAuthorInitial = (note) => {
  const text = String(note?.author_display_name || note?.artist_name || note?.admin_full_name || '不').trim();
  return (text.charAt(0) || '不').toUpperCase();
};

const formatNoteImageExpiryLabel = (expiresAt) => {
  const formatted = formatDateTimeJST(expiresAt);
  if (!formatted) return '';
  const currentYear = new Date().getFullYear();
  const yearPrefix = `${currentYear}/`;
  const displayValue = formatted.startsWith(yearPrefix) ? formatted.slice(yearPrefix.length) : formatted;
  return `画像期限: ${displayValue}`;
};

const formatNoteAttachmentExpiryLabel = (expiresAt) => {
  if (!expiresAt) return '保存期限なし';
  return formatNoteImageExpiryLabel(expiresAt).replace(/^画像期限:/, '保存期限:');
};

const getNoteImageDownloadName = (image, index) => {
  const preferred = String(image?.original_name || image?.file_name || '').trim();
  if (preferred) return preferred;
  return `note-image-${index + 1}.jpg`;
};

const pickNoteImagePreviewSrc = (image) => image?.proxy_url || image?.proxyUrl || image?.url || '';
const pickNoteImageFullSrc = (image) => image?.url || image?.downloadUrl || image?.proxy_url || image?.proxyUrl || '';
const pickNoteImageFallbackSrc = (image) => {
  const previewSrc = pickNoteImagePreviewSrc(image);
  const fullSrc = pickNoteImageFullSrc(image);
  return fullSrc && fullSrc !== previewSrc ? fullSrc : '';
};

const NoteImage = ({ image, className = '', alt = '', loading }) => {
  const previewSrc = pickNoteImagePreviewSrc(image);
  const fallbackSrc = pickNoteImageFallbackSrc(image);
  const [src, setSrc] = useState(previewSrc);

  useEffect(() => {
    setSrc(previewSrc);
  }, [previewSrc]);

  if (!src) return null;

  return (
    <img
      className={className}
      src={src}
      alt={alt}
      loading={loading}
      onError={() => {
        if (fallbackSrc && src !== fallbackSrc) {
          setSrc(fallbackSrc);
        }
      }}
    />
  );
};

const NoteAttachmentImagePreview = ({ attachment }) => {
  const directSrc = pickNoteImagePreviewSrc(attachment);
  const directFallbackSrc = pickNoteImageFallbackSrc(attachment);
  const file = attachment?.file;
  const fileKey = String(attachment?.file_key || '').trim();
  const fileName = attachment?.title || attachment?.file_name || file?.name || '画像';
  const [src, setSrc] = useState(directSrc);
  const [fallbackSrc, setFallbackSrc] = useState(directFallbackSrc);

  useEffect(() => {
    let disposed = false;
    let objectUrl = '';
    setSrc(directSrc);
    setFallbackSrc(directFallbackSrc);
    if (directSrc) return () => { disposed = true; };
    if (file && typeof URL.createObjectURL === 'function') {
      objectUrl = URL.createObjectURL(file);
      setSrc(objectUrl);
      return () => {
        disposed = true;
        if (objectUrl && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(objectUrl);
      };
    }
    if (fileKey) {
      createStorageProxyUrl({ key: fileKey, downloadName: fileName })
        .then((resolved) => {
          if (!disposed) setSrc(resolved?.previewUrl || resolved?.url || '');
        })
        .catch(() => {
          if (!disposed) setSrc('');
        });
    }
    return () => { disposed = true; };
  }, [directFallbackSrc, directSrc, file, fileKey, fileName]);

  return (
    <span className="note-attachment-preview__media">
      {src ? (
        <img
          src={src}
          alt={`${fileName}のプレビュー`}
          onError={() => {
            if (fallbackSrc && src !== fallbackSrc) {
              setSrc(fallbackSrc);
              setFallbackSrc('');
            } else {
              setSrc('');
            }
          }}
        />
      ) : (
        <i className="fa-solid fa-file-image" aria-hidden="true" />
      )}
    </span>
  );
};

const NoteAttachmentPreviewCard = ({ attachment, removed = false, subtitle, actionLabel, onAction }) => {
  const image = isImageAttachment(attachment);
  const fileName = attachment?.title || attachment?.original_name || attachment?.file_name || attachment?.file?.name || '添付ファイル';
  return (
    <div className={`note-attachment-preview note-attachment-preview--${image ? 'image' : 'file'}${removed ? ' is-removed' : ''}`}>
      {image ? (
        <NoteAttachmentImagePreview attachment={attachment} />
      ) : (
        <span className="note-attachment-preview__icon">
          <i className={`fa-solid ${getFileIcon(attachment?.file_name || attachment?.file?.name)}`} aria-hidden="true" />
        </span>
      )}
      <span className="note-attachment-preview__content">
        <span className="note-attachment-preview__main">
          <strong title={fileName}>{fileName}</strong>
          <small>{subtitle}</small>
        </span>
        <DsButton type="button" variant="secondary" size="sm" onClick={onAction}>
          {actionLabel}
        </DsButton>
      </span>
    </div>
  );
};

const ChatNotesPanel = ({
  ownerId,
  rootClassName,
  title = 'ノート',
  buildListUrl,
  createUrl,
  buildUpdateUrl,
  buildDeleteUrl,
  buildDetailUrl,
  onModeChange,
  openDetailEventName = '',
  closeDetailEventName = '',
  closeImageEventName = '',
  emptyText = 'ノートはまだありません。',
  onNotesChanged,
  canCreateNote = true,
  canManageNotes = true,
  enableEventAttachments = false,
  canUploadEventMaterials = false,
  canSelectEventMaterials = false,
  canDeleteEventMaterials = false,
  eventMaterialRole = 'admin',
}) => {
  const [notes, setNotes] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const confirmDialog = useConfirm();
  const { success: toastSuccess, error: toastError } = useToast();
  const pdfPreview = usePdfPreview();
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('list');
  const [detail, setDetail] = useState(null);
  const [titleValue, setTitleValue] = useState('');
  const [bodyValue, setBodyValue] = useState('');
  const [existingImages, setExistingImages] = useState([]);
  const [removedOrders, setRemovedOrders] = useState(new Set());
  const [newImages, setNewImages] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState(new Set());
  const [newAttachments, setNewAttachments] = useState([]);
  const [retentionEnabled, setRetentionEnabled] = useState(true);
  const [materialPickerOpen, setMaterialPickerOpen] = useState(false);
  const [deleteAttachmentTarget, setDeleteAttachmentTarget] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalIndex, setImageModalIndex] = useState(0);
  const fileRef = useRef(null);
  const attachmentFileRef = useRef(null);
  const offsetRef = useRef(0);
  const loadingRef = useRef(false);
  const onModeChangeRef = useRef(onModeChange);
  const onNotesChangedRef = useRef(onNotesChanged);
  const buildListUrlRef = useRef(buildListUrl);
  const buildDetailUrlRef = useRef(buildDetailUrl);
  const buildUpdateUrlRef = useRef(buildUpdateUrl);
  const buildDeleteUrlRef = useRef(buildDeleteUrl);

  const limit = 20;

  useEffect(() => {
    onModeChangeRef.current = onModeChange;
  }, [onModeChange]);

  useEffect(() => {
    onNotesChangedRef.current = onNotesChanged;
  }, [onNotesChanged]);

  useEffect(() => {
    buildListUrlRef.current = buildListUrl;
  }, [buildListUrl]);

  useEffect(() => {
    buildDetailUrlRef.current = buildDetailUrl;
  }, [buildDetailUrl]);

  useEffect(() => {
    buildUpdateUrlRef.current = buildUpdateUrl;
  }, [buildUpdateUrl]);

  useEffect(() => {
    buildDeleteUrlRef.current = buildDeleteUrl;
  }, [buildDeleteUrl]);

  const emitMode = useCallback((nextMode) => {
    setMode(nextMode);
    if (typeof onModeChangeRef.current === 'function') {
      onModeChangeRef.current(nextMode);
    }
  }, []);

  const resetCompose = useCallback(() => {
    setTitleValue('');
    setBodyValue('');
    setExistingImages([]);
    setRemovedOrders(new Set());
    setNewImages([]);
    setExistingAttachments([]);
    setRemovedAttachmentIds(new Set());
    setNewAttachments([]);
    setRetentionEnabled(true);
    setMaterialPickerOpen(false);
    setDeleteAttachmentTarget(null);
    if (fileRef.current) fileRef.current.value = '';
    if (attachmentFileRef.current) attachmentFileRef.current.value = '';
  }, []);

  const fetchNotes = useCallback(async (reset = false) => {
    if (!ownerId || loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const nextOffset = reset ? 0 : offsetRef.current;
      const json = await listChatNotesByUrl(buildListUrlRef.current(ownerId, nextOffset, limit));
      const rows = Array.isArray(json?.notes) ? json.notes : [];
      const draftRows = Array.isArray(json?.drafts) ? json.drafts : [];
      setNotes((prev) => (reset ? rows : [...prev, ...rows]));
      if (reset) setDrafts(draftRows);
      const resolvedOffset = nextOffset + rows.length;
      offsetRef.current = resolvedOffset;
      setHasMore(rows.length === limit);
    } catch (error) {
      console.error('notes fetch error:', error);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [limit, ownerId]);

  const openDetail = useCallback(async (noteId, options = {}) => {
    if (!noteId) return;
    try {
      const baseUrl = buildDetailUrlRef.current(noteId);
      const detailUrl = options.version === 'draft'
        ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}version=draft`
        : baseUrl;
      const json = await getChatNoteDetailByUrl(detailUrl);
      setDetail(json);
      emitMode({ type: 'detail', noteId });
    } catch (error) {
      console.error('note detail error:', error);
      toastError('ノート情報の取得に失敗しました。');
    }
  }, [toastError, emitMode]);

  useEffect(() => {
    setNotes([]);
    setDrafts([]);
    offsetRef.current = 0;
    setHasMore(true);
    setDetail(null);
    resetCompose();
    emitMode('list');
    if (ownerId) {
      fetchNotes(true);
    }
  }, [ownerId, emitMode, fetchNotes, resetCompose]);

  useEffect(() => {
    if (!openDetailEventName) return undefined;
    const handleOpen = (event) => {
      const noteId = event?.detail?.noteId;
      if (noteId) openDetail(noteId);
    };
    window.addEventListener(openDetailEventName, handleOpen);
    return () => window.removeEventListener(openDetailEventName, handleOpen);
  }, [openDetailEventName, openDetail]);

  useEffect(() => {
    if (!closeDetailEventName) return undefined;
    const handleClose = () => {
      setDetail(null);
      resetCompose();
      emitMode('list');
    };
    window.addEventListener(closeDetailEventName, handleClose);
    return () => window.removeEventListener(closeDetailEventName, handleClose);
  }, [closeDetailEventName, emitMode, resetCompose]);

  useEffect(() => {
    if (!closeImageEventName) return undefined;
    const handleClose = () => {
      setImageModalOpen(false);
      if (detail?.note?.note_id) emitMode({ type: 'detail', noteId: detail.note.note_id });
    };
    window.addEventListener(closeImageEventName, handleClose);
    return () => window.removeEventListener(closeImageEventName, handleClose);
  }, [closeImageEventName, detail, emitMode]);

  const uploadViaServer = async (file) => {
    return uploadNoteImageThroughApi(file);
  };

  const newImagePreviewUrls = useMemo(
    () => newImages.map((file) => ({
      file,
      url: typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '',
    })),
    [newImages]
  );

  useEffect(() => {
    return () => {
      if (typeof URL.revokeObjectURL !== 'function') return;
      newImagePreviewUrls.forEach((preview) => {
        if (preview.url) URL.revokeObjectURL(preview.url);
      });
    };
  }, [newImagePreviewUrls]);

  const uploadNoteImage = async (file) => {
    const uploaded = await uploadViaServer(file);
    return {
      key: uploaded?.key,
      originalName: uploaded?.originalName || file.name,
      contentType: uploaded?.contentType || file.type,
    };
  };

  const uploadEventNoteAttachment = async (file) => {
    const uploaded = await uploadMaterialThroughApi({ eventUuid: ownerId }, file);
    return {
      source: 'upload',
      fileKey: uploaded.key,
      fileName: uploaded.fileName || file.name,
      fileSize: uploaded.byteSize ?? file.size,
      contentType: uploaded.contentType || file.type || 'application/octet-stream',
    };
  };

  const handleFiles = (event) => {
    const pickedFiles = Array.from(event.target.files || []);
    const imageFiles = pickedFiles.filter(isChatImageFile);
    if (pickedFiles.length !== imageFiles.length) {
      toastError('JPEG / PNG / WebP / AVIF / HEIC / HEIF画像のみ追加できます。');
    }
    const addCompatibleImages = (compatibleImages) => {
      const files = compatibleImages.filter((file) => file.size <= NOTE_IMAGE_MAX_BYTES);
      if (compatibleImages.length !== files.length) {
        toastError(`1枚あたり${NOTE_IMAGE_MAX_MB}MBまでの画像を選択してください。`);
      }
      const remain = MAX_IMAGES - (existingImages.length - removedOrders.size) - newImages.length;
      const picked = files.slice(0, Math.max(0, remain));
      if (files.length > picked.length) {
        toastError(`画像は合計${MAX_IMAGES}枚までです。`);
      }
      setNewImages((prev) => [...prev, ...picked]);
      if (fileRef.current) fileRef.current.value = '';
    };

    if (!imageFiles.some(isHeifChatImageFile)) {
      addCompatibleImages(imageFiles);
      return;
    }
    normalizeChatImagesForUpload(imageFiles)
      .then(addCompatibleImages)
      .catch((error) => {
        toastError(error?.message || '画像を送信できる形式へ変換できませんでした。');
        if (fileRef.current) fileRef.current.value = '';
      });
  };

  const handleAttachmentFiles = (event) => {
    const picked = Array.from(event.target.files || []);
    const accepted = picked.filter((file) => isAllowedFile(file) && (
      !/^image\//.test(file.type) || file.size <= NOTE_IMAGE_MAX_BYTES
    ));
    if (accepted.length !== picked.length) {
      toastError('画像は1件50MB、対応ファイルは1件100MBまでです。');
    }
    const currentCount = existingAttachments.length - removedAttachmentIds.size + newAttachments.length;
    const remaining = Math.max(0, MAX_EVENT_ATTACHMENTS - currentCount);
    const next = accepted.slice(0, remaining).map((file) => ({
      clientId: `upload-${Date.now()}-${Math.random()}`,
      source: 'upload',
      file,
      file_name: file.name,
      file_size: file.size,
      content_type: file.type || 'application/octet-stream',
    }));
    if (accepted.length > next.length) {
      toastError(`画像・ファイルは合計${MAX_EVENT_ATTACHMENTS}件までです。`);
    }
    setNewAttachments((current) => [...current, ...next]);
    if (attachmentFileRef.current) attachmentFileRef.current.value = '';
  };

  const handleMaterialPickerConfirm = (materials) => {
    setNewAttachments((current) => {
      const uploads = current.filter((item) => item.source !== 'event_material');
      const existingIds = new Set(
        existingAttachments.map((item) => Number(item.event_material_id)).filter(Number.isFinite)
      );
      const additions = materials
        .filter((material) => !existingIds.has(Number(material.id || material.event_material_id)))
        .map((material) => ({
          ...material,
          clientId: `material-${material.id || material.event_material_id}`,
          source: 'event_material',
          event_material_id: Number(material.id || material.event_material_id),
        }));
      return [...uploads, ...additions].slice(0, MAX_EVENT_ATTACHMENTS);
    });
    setMaterialPickerOpen(false);
  };

  const confirmPrivateMaterialDisclosure = async (saveMode) => {
    if (eventMaterialRole !== 'admin' || saveMode === 'draft') return true;
    const privateNewMaterials = newAttachments.filter((attachment) => (
      attachment.source === 'event_material' && attachment.effective_share_with_artists !== true
    ));
    const privateExistingMaterials = existingAttachments.filter((attachment) => (
      attachment.source_kind === 'event_material' &&
      !removedAttachmentIds.has(attachment.attachment_id) &&
      attachment.effective_share_with_artists !== true
    ));
    const privateCount = privateNewMaterials.length + privateExistingMaterials.length;
    if (!privateCount) return true;
    return confirmDialog({
      title: '非共有資料をノートで公開します',
      message: `非共有のイベント資料${privateCount}件は、ノートを閲覧できるアーティストにも表示されます。公開して保存しますか？`,
      confirmLabel: '公開して保存',
      cancelLabel: '選択を見直す',
    });
  };

  const handleCreate = async (saveMode = 'publish') => {
    if (!canCreateNote) return;
    if (!ownerId) return;
    const trimmedTitle = titleValue.trim();
    const trimmedBody = bodyValue.trim();
    if (!trimmedTitle || !trimmedBody) {
      toastError('タイトルと本文を入力してください。');
      return;
    }
    if (!(await confirmPrivateMaterialDisclosure(saveMode))) return;
    try {
      setSaving(true);
      const uploaded = [];
      for (const file of newImages) {
        uploaded.push(await uploadNoteImage(file));
      }
      const uploadedAttachments = [];
      for (const attachment of newAttachments) {
        if (attachment.source === 'upload') {
          uploadedAttachments.push(await uploadEventNoteAttachment(attachment.file));
        } else {
          uploadedAttachments.push({
            source: 'event_material',
            materialId: attachment.event_material_id,
          });
        }
      }
      await createChatNoteByUrl(createUrl, {
        threadId: ownerId,
        eventId: ownerId,
        title: trimmedTitle,
        content: trimmedBody,
        images: uploaded,
        ...(enableEventAttachments ? {
          attachments: uploadedAttachments,
          retentionMode: retentionEnabled ? 'default' : 'none',
        } : {}),
        saveMode,
      });
      resetCompose();
      setDetail(null);
      emitMode('list');
      await fetchNotes(true);
      if (typeof onNotesChangedRef.current === 'function') {
        onNotesChangedRef.current({ type: saveMode === 'draft' ? 'draft-save' : 'create' });
      }
    } catch (error) {
      console.error('note create error:', error);
      toastError(getNoteErrorMessage(error, 'ノート作成に失敗しました。'));
    } finally {
      setSaving(false);
    }
  };

  const enterEdit = useCallback(async () => {
    if (!canManageNotes) return;
    if (!detail?.note) return;
    let editDetail = detail;
    const draftNoteId = String(detail.note.draft_note_id || '').trim();
    if (draftNoteId) {
      try {
        const baseUrl = buildDetailUrlRef.current(draftNoteId);
        const draftUrl = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}version=draft`;
        editDetail = await getChatNoteDetailByUrl(draftUrl);
        setDetail(editDetail);
      } catch (error) {
        console.error('draft detail fetch error:', error);
        toastError(getNoteErrorMessage(error, '下書きの取得に失敗しました。'));
        return;
      }
    }
    setTitleValue(editDetail.note.title || '');
    setBodyValue(editDetail.note.content || '');
    setExistingImages(Array.isArray(editDetail.images) ? editDetail.images : []);
    setExistingAttachments(Array.isArray(editDetail.attachments) ? editDetail.attachments : []);
    setRetentionEnabled(editDetail.note.attachment_retention_mode !== 'none');
    setRemovedOrders(new Set());
    setRemovedAttachmentIds(new Set());
    setNewImages([]);
    setNewAttachments([]);
    emitMode({ type: 'edit', note: editDetail.note });
  }, [canManageNotes, detail, emitMode, toastError]);

  const enterDraftEditById = useCallback(async (draftNoteId) => {
    if (!canManageNotes) return;
    const normalizedDraftNoteId = String(draftNoteId || '').trim();
    if (!normalizedDraftNoteId) return;
    try {
      const baseUrl = buildDetailUrlRef.current(normalizedDraftNoteId);
      const draftUrl = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}version=draft`;
      const draftDetail = await getChatNoteDetailByUrl(draftUrl);
      setDetail(draftDetail);
      setTitleValue(draftDetail.note?.title || '');
      setBodyValue(draftDetail.note?.content || '');
      setExistingImages(Array.isArray(draftDetail.images) ? draftDetail.images : []);
      setExistingAttachments(Array.isArray(draftDetail.attachments) ? draftDetail.attachments : []);
      setRetentionEnabled(draftDetail.note?.attachment_retention_mode !== 'none');
      setRemovedOrders(new Set());
      setRemovedAttachmentIds(new Set());
      setNewImages([]);
      setNewAttachments([]);
      emitMode({ type: 'edit', note: draftDetail.note });
    } catch (error) {
      console.error('draft detail fetch error:', error);
      toastError(getNoteErrorMessage(error, '下書きの取得に失敗しました。'));
    }
  }, [canManageNotes, emitMode, toastError]);

  const handleUpdate = async (saveMode = 'publish') => {
    if (!canManageNotes) return;
    const noteId = detail?.note?.note_id;
    if (!noteId) return;
    if (!(await confirmPrivateMaterialDisclosure(saveMode))) return;
    try {
      setSaving(true);
      const uploaded = [];
      for (const file of newImages) {
        uploaded.push(await uploadNoteImage(file));
      }
      const uploadedAttachments = [];
      for (const attachment of newAttachments) {
        if (attachment.source === 'upload') {
          uploadedAttachments.push(await uploadEventNoteAttachment(attachment.file));
        } else {
          uploadedAttachments.push({
            source: 'event_material',
            materialId: attachment.event_material_id,
          });
        }
      }
      await updateChatNoteByUrl(buildUpdateUrlRef.current(noteId), {
        title: titleValue.trim() || null,
        content: bodyValue.trim() || null,
        removeImages: Array.from(removedOrders.values()).sort((a, b) => a - b),
        addImages: uploaded,
        ...(enableEventAttachments ? {
          addAttachments: uploadedAttachments,
          removeAttachmentIds: Array.from(removedAttachmentIds.values()),
          retentionMode: retentionEnabled ? 'default' : 'none',
        } : {}),
        saveMode,
      });
      resetCompose();
      setDetail(null);
      emitMode('list');
      await fetchNotes(true);
      if (typeof onNotesChangedRef.current === 'function') {
        onNotesChangedRef.current({ type: saveMode === 'draft' ? 'draft-save' : 'update', noteId });
      }
    } catch (error) {
      console.error('note update error:', error);
      toastError(getNoteErrorMessage(error, 'ノート更新に失敗しました。'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!canManageNotes) return;
    if (!noteId) return;
    if (!(await confirmDialog({ message: 'このノートを削除しますか？', danger: true }))) return;
    try {
      setSaving(true);
      await deleteChatNoteByUrl(buildDeleteUrlRef.current(noteId));
      resetCompose();
      setDetail(null);
      emitMode('list');
      await fetchNotes(true);
    } catch (error) {
      console.error('note delete error:', error);
      toastError(getNoteErrorMessage(error, 'ノート削除に失敗しました。'));
    } finally {
      setSaving(false);
    }
  };

  const openImageModal = (index) => {
    setImageModalIndex(index);
    setImageModalOpen(true);
    emitMode({ type: 'imgmodal', idx: index });
  };

  const downloadNoteImage = useCallback(async (image, index) => {
    const href = image?.url || image?.proxy_url || '';
    if (!href) return;
    try {
      const blob = await downloadBlobFromUrl(href, 'include');
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = getNoteImageDownloadName(image, index);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      const link = document.createElement('a');
      link.href = href;
      link.download = getNoteImageDownloadName(image, index);
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  }, []);

  const handleDeleteImage = useCallback(async (image) => {
    if (!canManageNotes) return;
    const noteId = detail?.note?.note_id;
    const imageOrder = Number(image?.image_order);
    if (!noteId || !Number.isFinite(imageOrder)) return;
    if (!(await confirmDialog({ message: 'この画像を削除しますか？', danger: true }))) return;
    try {
      setSaving(true);
      await updateChatNoteByUrl(buildUpdateUrlRef.current(noteId), {
        title: null,
        content: null,
        removeImages: [imageOrder],
        addImages: [],
      });
      setImageModalOpen(false);
      await fetchNotes(true);
      await openDetail(noteId);
      if (typeof onNotesChangedRef.current === 'function') {
        onNotesChangedRef.current({ type: 'image-delete', noteId, imageOrder });
      }
    } catch (error) {
      console.error('note image delete error:', error);
      toastError(getNoteErrorMessage(error, '画像削除に失敗しました。'));
    } finally {
      setSaving(false);
    }
  }, [toastError, confirmDialog, canManageNotes, detail, fetchNotes, openDetail]);

  const openEventNoteMaterial = useCallback((attachment) => {
    openOrSaveEventMaterial(
      {
        id: attachment?.event_material_id,
        title: attachment?.title,
        file_name: attachment?.file_name,
        file_key: attachment?.file_key,
        file_size: attachment?.file_size,
        content_type: attachment?.content_type,
        url: attachment?.url,
        preview_url: attachment?.preview_url,
      },
      { pdfPreview, onSuccess: toastSuccess, onError: toastError, preferDirectUrl: true }
    );
  }, [pdfPreview, toastError, toastSuccess]);

  const deleteEventNoteAttachment = useCallback(async (mode) => {
    if (!canManageNotes || !deleteAttachmentTarget) return;
    const noteId = detail?.note?.note_id;
    const attachmentId = deleteAttachmentTarget.attachment_id;
    if (!noteId || !attachmentId) return;
    try {
      setSaving(true);
      await updateChatNoteByUrl(buildUpdateUrlRef.current(noteId), {
        title: null,
        content: null,
        removeImages: [],
        addImages: [],
        removeAttachmentIds: mode === 'unlink' ? [attachmentId] : [],
        deleteMaterialAttachmentIds: mode === 'delete-material' ? [attachmentId] : [],
      });
      setDeleteAttachmentTarget(null);
      await fetchNotes(true);
      await openDetail(noteId);
      toastSuccess(mode === 'unlink' ? 'ノートとの紐付けを解除しました。' : 'イベント資料から削除しました。');
    } catch (error) {
      console.error('note attachment delete error:', error);
      toastError(getNoteErrorMessage(error, '添付ファイルを削除できませんでした。'));
    } finally {
      setSaving(false);
    }
  }, [canManageNotes, deleteAttachmentTarget, detail, fetchNotes, openDetail, toastError, toastSuccess]);

  const attachmentItems = useMemo(
    () => Array.isArray(detail?.attachments) ? detail.attachments : [],
    [detail]
  );
  const attachmentImageItems = useMemo(
    () => attachmentItems.filter((item) => item?.display_kind === 'image'),
    [attachmentItems]
  );
  const attachmentFileItems = useMemo(
    () => attachmentItems.filter((item) => item?.display_kind !== 'image'),
    [attachmentItems]
  );
  const imageItems = useMemo(() => [
    ...(Array.isArray(detail?.images) ? detail.images.map((image) => ({ ...image, source_kind: 'legacy_image' })) : []),
    ...attachmentImageItems,
  ], [attachmentImageItems, detail]);
  const selectedImage = imageItems[imageModalIndex] || null;
  const selectedImageSrc = pickNoteImageFullSrc(selectedImage);
  const selectedImageFallbackSrc = pickNoteImageFallbackSrc(selectedImage);
  const standaloneDrafts = useMemo(() => {
    if (!canManageNotes) return [];
    return drafts.filter((draft) => {
      const draftOfNoteId = String(draft?.draft_of_note_id || draft?.draftOfNoteId || '').trim();
      const canEdit = draft?.permissions?.can_edit ?? draft?.permissions?.canEdit;
      return !draftOfNoteId && canEdit !== false;
    });
  }, [canManageNotes, drafts]);

  const renderList = () => (
    <div className="notes-list-area">
      {!notes.length && !standaloneDrafts.length && loading ? (
        <ul className="notes-list" role="status" aria-live="polite">
          {Array.from({ length: 3 }, (_, index) => (
            <li key={`note-skeleton-${index}`} className="note-card notes-card" aria-hidden="true">
              <div className="note-card__main">
                <SkeletonLine width={index === 1 ? '58%' : '74%'} height={14} />
                <SkeletonLine width="92%" height={11} />
                <SkeletonLine width={index === 2 ? '64%' : '82%'} height={11} />
                {index === 0 ? (
                  <div className="note-card__thumb-wrap">
                    <SkeletonMedia className="note-card__thumb" />
                  </div>
                ) : null}
                <div className="note-card__meta">
                  <SkeletonAvatar className="note-avatar note-avatar--lg" size={32} />
                  <SkeletonLine width={96} height={11} />
                  <SkeletonLine width={72} height={10} />
                </div>
              </div>
              <div className="note-card__actions">
                <SkeletonButton width={32} height={30} />
              </div>
            </li>
          ))}
          <span className="sr-only">ノートを読み込み中...</span>
        </ul>
      ) : (
        <>
          {standaloneDrafts.length ? (
            <section className="notes-drafts-section" aria-labelledby="notes-drafts-heading">
              <div id="notes-drafts-heading" className="notes-drafts-heading">
                下書き {standaloneDrafts.length}件
              </div>
              <ul className="notes-list">
                {standaloneDrafts.map((draft) => {
                  const authorImage = buildProfileImageUrl(draft);
                  return (
                    <li key={draft.note_id} className="note-card notes-card is-draft">
                      <button
                        className="note-card__main"
                        type="button"
                        onClick={() => enterDraftEditById(draft.note_id)}
                        title="下書きを編集"
                      >
                        <div className="note-card__title">
                          {draft.title || '無題のノート'}
                          <span className="note-card__draft-badge">下書き</span>
                        </div>
                        <div className="note-card__meta">
                          <span className="note-avatar note-avatar--lg" style={authorImage ? { backgroundImage: `url(${authorImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}>
                            {!authorImage ? getAuthorInitial(draft) : null}
                          </span>
                          <span className="note-author">{draft.author_display_name || '作成者不明'}</span>
                          <span className="note-time">{formatDateTimeJST(draft.updated_at || draft.created_at)}</span>
                        </div>
                      </button>
                      <div className="note-card__actions">
                        <button type="button" className="note-action" onClick={() => enterDraftEditById(draft.note_id)} title="下書きを編集">
                          <i className="fa-solid fa-pen-to-square" aria-hidden="true" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}
          {!notes.length && !standaloneDrafts.length ? (
            <div className="notes-empty">{emptyText}</div>
          ) : null}
          {notes.length ? (
            <ul className="notes-list">
              {notes.map((note) => {
                const thumbUrl = note.first_image_proxy_url || note.first_image_url || '';
                const moreCount = Math.max(0, Number(note.image_count || 0) - 1);
                const authorImage = buildProfileImageUrl(note);
                return (
                  <li key={note.note_id} className="note-card notes-card">
                    <button data-selection-safe-navigation className="note-card__main" type="button" onClick={() => openDetail(note.note_id)} title="ノートを開く">
                      {note.title ? (
                        <div className="note-card__title">
                          {note.title}
                          {note.draft_note_id ? <span className="note-card__draft-badge">下書きあり</span> : null}
                        </div>
                      ) : null}
                      {note.content ? <div className="note-card__body">{note.content}</div> : null}
                      {thumbUrl ? (
                        <div className="note-card__thumb-wrap">
                          <NoteImage
                            image={{ proxy_url: thumbUrl, url: note.first_image_url }}
                            className="note-card__thumb"
                            alt=""
                            loading="lazy"
                          />
                          {moreCount > 0 ? <span className="note-card__thumb-count">+{moreCount}枚</span> : null}
                        </div>
                      ) : null}
                      <div className="note-card__meta">
                        <span className="note-avatar note-avatar--lg" style={authorImage ? { backgroundImage: `url(${authorImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}>
                          {!authorImage ? getAuthorInitial(note) : null}
                        </span>
                        <span className="note-author">{note.author_display_name || '作成者不明'}</span>
                        <span className="note-time">{formatDateTimeJST(note.created_at)}</span>
                      </div>
                    </button>
                    <div className="note-card__actions">
                      {canManageNotes && note?.permissions?.can_edit ? (
                        <button data-selection-safe-navigation type="button" className="note-action" onClick={() => openDetail(note.note_id)} title="詳細を開く">
                          <i className="fa-solid fa-pen-to-square" aria-hidden="true" />
                        </button>
                      ) : null}
                      {canManageNotes && note?.permissions?.can_delete ? (
                        <button type="button" className="note-action delete" onClick={() => handleDelete(note.note_id)} title="削除">
                          <i className="fa-solid fa-trash" aria-hidden="true" />
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </>
      )}
      {loading && notes.length ? (
        <div className="notes-loadmore" role="status" aria-live="polite">読み込み中…</div>
      ) : null}
      {hasMore && !loading ? (
        <div className="notes-loadmore">
          <DsButton type="button" variant="secondary" size="sm" onClick={() => fetchNotes(false)}>もっと見る</DsButton>
        </div>
      ) : null}
    </div>
  );

  const renderForm = (isEdit) => {
    const activeCount = existingImages.filter((image) => !removedOrders.has(Number(image.image_order))).length + newImages.length;
    const activeAttachmentCount = existingImages.filter((image) => !removedOrders.has(Number(image.image_order))).length +
      existingAttachments.filter((attachment) => !removedAttachmentIds.has(attachment.attachment_id)).length +
      newAttachments.length;
    const existingAttachmentImages = existingAttachments.filter(isImageAttachment);
    const existingAttachmentFiles = existingAttachments.filter((attachment) => !isImageAttachment(attachment));
    const newAttachmentEntries = newAttachments.map((attachment, index) => ({ attachment, index }));
    const newAttachmentImages = newAttachmentEntries.filter(({ attachment }) => isImageAttachment(attachment));
    const newAttachmentFiles = newAttachmentEntries.filter(({ attachment }) => !isImageAttachment(attachment));
    const renderExistingAttachment = (attachment) => {
      const removed = removedAttachmentIds.has(attachment.attachment_id);
      return (
        <NoteAttachmentPreviewCard
          key={attachment.attachment_id}
          attachment={attachment}
          removed={removed}
          subtitle={formatNoteAttachmentExpiryLabel(attachment.expires_at)}
          actionLabel={removed ? '戻す' : '外す'}
          onAction={() => setRemovedAttachmentIds((current) => {
            const next = new Set(current);
            if (removed) next.delete(attachment.attachment_id);
            else next.add(attachment.attachment_id);
            return next;
          })}
        />
      );
    };
    const renderNewAttachment = ({ attachment, index }) => (
      <NoteAttachmentPreviewCard
        key={attachment.clientId || index}
        attachment={attachment}
        subtitle={attachment.source === 'event_material' ? 'イベント資料を紐付け' : formatBytes(attachment.file_size)}
        actionLabel="外す"
        onAction={() => setNewAttachments((current) => current.filter((_, itemIndex) => itemIndex !== index))}
      />
    );
    return (
      <div className="notes-form-area">
        <div className="notes-form-row">
          <label className="notes-form-label">タイトル</label>
          <input className="notes-input" value={titleValue} onChange={(event) => setTitleValue(event.target.value)} maxLength={120} />
        </div>
        <div className="notes-form-row">
          <label className="notes-form-label">本文</label>
          <textarea className="notes-textarea" rows={8} value={bodyValue} onChange={(event) => setBodyValue(event.target.value)} />
        </div>
        {enableEventAttachments ? (
          <div className="notes-form-row">
            <label className="notes-form-label">画像・ファイル</label>
            <div className="note-attachment-tools">
              <div className="notes-img-hint">現在 {activeAttachmentCount} / {MAX_EVENT_ATTACHMENTS} 件</div>
              <div className="note-attachment-tools__actions">
                {canUploadEventMaterials ? (
                  <DsButton type="button" variant="create" size="sm" onClick={() => attachmentFileRef.current?.click()}>
                    アップロード
                  </DsButton>
                ) : null}
                {canSelectEventMaterials ? (
                  <DsButton type="button" variant="secondary" size="sm" onClick={() => setMaterialPickerOpen(true)}>
                    イベント資料から選択
                  </DsButton>
                ) : null}
                <input
                  ref={attachmentFileRef}
                  type="file"
                  multiple
                  hidden
                  onChange={handleAttachmentFiles}
                />
              </div>
              <div className="note-attachment-retention">
                <DsSwitch
                  checked={retentionEnabled}
                  onChange={setRetentionEnabled}
                  label="一定期間後に削除"
                  ariaLabel="添付ファイルを一定期間後に削除"
                />
                <small>
                  {retentionEnabled
                    ? '従来と同じ保存期限が自動で入ります。'
                    : '保存期限を設定せず、手動で削除するまで保持します。'}
                </small>
              </div>
              {existingImages.length || existingAttachmentImages.length || newAttachmentImages.length ? (
                <section className="note-attachment-preview-group" aria-labelledby="note-form-images-title">
                  <h3 id="note-form-images-title" className="note-attachment-section-title">画像</h3>
                  <div className="note-attachment-preview-list">
                    {existingImages.map((image) => {
                    const imageOrder = Number(image.image_order || 0);
                    const removed = removedOrders.has(imageOrder);
                    return (
                      <NoteAttachmentPreviewCard
                        key={`legacy-${image.file_name}-${imageOrder}`}
                        attachment={{ ...image, display_kind: 'image' }}
                        removed={removed}
                        subtitle={formatNoteAttachmentExpiryLabel(image.expires_at)}
                        actionLabel={removed ? '戻す' : '外す'}
                        onAction={() => setRemovedOrders((current) => {
                          const next = new Set(current);
                          if (removed) next.delete(imageOrder);
                          else next.add(imageOrder);
                          return next;
                        })}
                      />
                    );
                    })}
                    {existingAttachmentImages.map(renderExistingAttachment)}
                    {newAttachmentImages.map(renderNewAttachment)}
                  </div>
                </section>
              ) : null}
              {existingAttachmentFiles.length || newAttachmentFiles.length ? (
                <section className="note-attachment-preview-group" aria-labelledby="note-form-files-title">
                  <h3 id="note-form-files-title" className="note-attachment-section-title">ファイル</h3>
                  <div className="note-attachment-preview-list">
                    {existingAttachmentFiles.map(renderExistingAttachment)}
                    {newAttachmentFiles.map(renderNewAttachment)}
                  </div>
                </section>
              ) : null}
            </div>
          </div>
        ) : null}
        {!enableEventAttachments && existingImages.length ? (
          <div className="notes-form-row">
            <label className="notes-form-label">既存画像</label>
            <div className="notes-existing-images">
              {existingImages.map((image) => {
                const imageOrder = Number(image.image_order || 0);
                const removed = removedOrders.has(imageOrder);
                const expiryLabel = formatNoteImageExpiryLabel(image.expires_at);
                return (
                  <div key={`${image.file_name}-${imageOrder}`} className={`notes-existing-thumb${removed ? ' is-removed' : ''}`}>
                    <NoteImage image={image} alt="" />
                    {expiryLabel ? <div className="notes-existing-expiry">{expiryLabel}</div> : null}
                    {!removed ? (
                      <button type="button" className="notes-existing-remove" onClick={() => setRemovedOrders((prev) => new Set(prev).add(imageOrder))}>×</button>
                    ) : (
                      <button type="button" className="notes-existing-undo" onClick={() => setRemovedOrders((prev) => {
                        const next = new Set(prev);
                        next.delete(imageOrder);
                        return next;
                      })}>戻す</button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
        {!enableEventAttachments ? <div className="notes-form-row">
          <label className="notes-form-label">画像を追加（最大 {MAX_IMAGES} 枚）</label>
          <div className="notes-img-hint">現在 {activeCount} / {MAX_IMAGES} 枚</div>
          <input ref={fileRef} type="file" accept={CHAT_IMAGE_ACCEPT_ATTR} multiple onChange={handleFiles} />
          {newImagePreviewUrls.length ? (
            <div className="notes-images-preview">
              {newImagePreviewUrls.map((preview, index) => (
                <span key={`${preview.file.name}-${index}`} className="notes-image-chip">
                  <img src={preview.url} alt="" className="notes-image-preview-thumb" />
                  <button
                    type="button"
                    className="notes-chip-remove"
                    aria-label={`${preview.file.name || '画像'}を削除`}
                    onClick={() => setNewImages((prev) => prev.filter((_, fileIndex) => fileIndex !== index))}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          ) : null}
        </div> : null}
        <div className="notes-form-actions">
          <DsButton type="button" variant="secondary" className="notes-secondary-action" onClick={() => (isEdit ? handleUpdate('draft') : handleCreate('draft'))} disabled={saving}>
            {saving ? '保存中…' : '下書き保存'}
          </DsButton>
          <DsButton type="button" variant="primary" block className="notes-submit-action" onClick={() => (isEdit ? handleUpdate('publish') : handleCreate('publish'))} disabled={saving}>
            {saving ? '保存中…' : '公開する'}
          </DsButton>
        </div>
      </div>
    );
  };

  const renderDetail = () => {
    if (!detail?.note) return null;
    const note = detail.note;
    const authorImage = buildProfileImageUrl(note);
    return (
      <div className="notes-detail-area">
        <div className="nd-header">
          <div className="nd-header__main">
            <div className="nd-title">{note.title}</div>
            <div className="nd-meta">
              <span className="note-avatar note-avatar--lg" style={authorImage ? { backgroundImage: `url(${authorImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}>
                {!authorImage ? getAuthorInitial(note) : null}
              </span>
              <span>{note.author_display_name || '作成者不明'}</span>
              <span>{formatDateTimeJST(note.updated_at || note.created_at)}</span>
            </div>
          </div>
          <div className="nd-actions">
            {canManageNotes && note?.draft_note_id ? (
              <button type="button" className="note-action" onClick={enterEdit} title="下書きを編集">
                <i className="fa-solid fa-file-pen" aria-hidden="true" />
              </button>
            ) : null}
            {canManageNotes && detail?.permissions?.can_edit ? (
              <button type="button" className="note-action" onClick={enterEdit} title="編集">
                <i className="fa-solid fa-pen-to-square" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        </div>
        <div className="nd-content">{renderContentWithLinks(note.content)}</div>
        {imageItems.length ? (
          <>
            {enableEventAttachments ? <h3 className="note-attachment-section-title">画像</h3> : null}
            <div className="nd-gallery">
              {imageItems.map((image, index) => {
                const isLegacyImage = image.source_kind === 'legacy_image';
                const expiryLabel = isLegacyImage
                  ? formatNoteImageExpiryLabel(image.expires_at)
                  : formatNoteAttachmentExpiryLabel(image.expires_at);
                return (
                <div key={image.attachment_id || `${image.file_name}-${index}`} className="nd-gallery__card">
                  <div className="nd-gallery__media">
                    <button
                      type="button"
                      className="nd-gallery__item"
                      aria-label={`${index + 1}枚目の画像を拡大`}
                      onClick={() => openImageModal(index)}
                    >
                      <NoteImage image={image} alt="" />
                    </button>
                  </div>
                  {expiryLabel ? <div className="nd-gallery__expiry">{expiryLabel}</div> : null}
                  <div className="nd-gallery__actions">
                    <DsButton
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => downloadNoteImage(image, index)}
                    >
                      保存
                    </DsButton>
                    {canManageNotes && detail?.permissions?.can_edit && !note?.draft_note_id ? (
                      <DsButton
                        type="button"
                        variant="danger"
                        size="sm"
                        onClick={() => isLegacyImage ? handleDeleteImage(image) : setDeleteAttachmentTarget(image)}
                        loading={saving}
                      >
                        削除
                      </DsButton>
                    ) : null}
                  </div>
                </div>
                );
              })}
            </div>
          </>
        ) : null}
        {attachmentFileItems.length ? (
          <section className="note-attachment-files" aria-labelledby="note-attachment-files-title">
            <h3 id="note-attachment-files-title" className="note-attachment-section-title">ファイル</h3>
            {attachmentFileItems.map((attachment) => (
              <div key={attachment.attachment_id} className="note-file-row">
                <span className="note-file-row__icon">
                  <i className={`fa-solid ${getFileIcon(attachment.file_name)}`} aria-hidden="true" />
                </span>
                <span className="note-file-row__main">
                  <strong>{attachment.title || attachment.file_name}</strong>
                  <span className="note-file-row__meta">
                    {[attachment.file_name, formatBytes(attachment.file_size), formatNoteAttachmentExpiryLabel(attachment.expires_at)].filter(Boolean).join(' ・ ')}
                  </span>
                </span>
                <span className="note-file-row__actions">
                  <DsButton type="button" variant="secondary" size="sm" onClick={() => openEventNoteMaterial(attachment)}>
                    開く
                  </DsButton>
                  {canManageNotes && detail?.permissions?.can_edit && !note?.draft_note_id ? (
                    <DsButton type="button" variant="danger" size="sm" onClick={() => setDeleteAttachmentTarget(attachment)}>
                      削除
                    </DsButton>
                  ) : null}
                </span>
              </div>
            ))}
          </section>
        ) : null}
      </div>
    );
  };

  return (
    <section className={rootClassName}>
      <div className="notes-header">
        <div className="notes-header__title">
          <i className="fa-solid fa-note-sticky" aria-hidden="true" />
          <span>{title}</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {mode !== 'list' ? (
            <button type="button" className="notes-back-action" onClick={() => emitMode('list')}>
              <i className="fa-solid fa-arrow-left" aria-hidden="true" />
              <span>戻る</span>
            </button>
          ) : null}
          {canCreateNote ? (
            <button type="button" className="notes-add-action" onClick={() => { resetCompose(); setDetail(null); emitMode('create'); }}>
              <span className="notes-add-action__icon"><i className="fa-solid fa-plus" aria-hidden="true" /></span>
              <span className="notes-add-action__label">ノートを作成</span>
            </button>
          ) : null}
        </div>
      </div>
      {mode === 'list' ? renderList() : null}
      {mode === 'create' ? renderForm(false) : null}
      {mode?.type === 'edit' ? renderForm(true) : null}
      {mode?.type === 'detail' ? renderDetail() : null}
      <ChatImageModal
        isOpen={imageModalOpen && imageItems.length > 0}
        src={selectedImageSrc}
        fallbackSrc={selectedImageFallbackSrc}
        onClose={() => {
          setImageModalOpen(false);
          if (detail?.note?.note_id) emitMode({ type: 'detail', noteId: detail.note.note_id });
        }}
        onPrev={imageItems.length > 1 ? () => setImageModalIndex((prev) => (prev - 1 + imageItems.length) % imageItems.length) : undefined}
        onNext={imageItems.length > 1 ? () => setImageModalIndex((prev) => (prev + 1) % imageItems.length) : undefined}
        showNav={imageItems.length > 1}
      />
      {enableEventAttachments ? (
        <EventNoteMaterialPicker
          open={materialPickerOpen}
          eventUuid={ownerId}
          role={eventMaterialRole}
          canUpload={canUploadEventMaterials}
          initialMaterials={newAttachments.filter((item) => item.source === 'event_material')}
          onClose={() => setMaterialPickerOpen(false)}
          onConfirm={handleMaterialPickerConfirm}
        />
      ) : null}
      <DsModal
        open={Boolean(deleteAttachmentTarget)}
        onClose={() => setDeleteAttachmentTarget(null)}
        title="添付ファイルを削除"
        size="sm"
      >
        <p>このノートから外すだけか、イベント資料からファイル自体を削除するか選択してください。</p>
        <div className="note-material-picker__actions">
          <DsButton type="button" variant="secondary" onClick={() => setDeleteAttachmentTarget(null)} disabled={saving}>
            キャンセル
          </DsButton>
          <DsButton type="button" variant="secondary" onClick={() => deleteEventNoteAttachment('unlink')} loading={saving}>
            紐付けだけ解除
          </DsButton>
          {canDeleteEventMaterials && deleteAttachmentTarget?.can_delete_material ? (
            <DsButton type="button" variant="danger" onClick={() => deleteEventNoteAttachment('delete-material')} loading={saving}>
              イベント資料から削除
            </DsButton>
          ) : null}
        </div>
      </DsModal>
    </section>
  );
};

export default ChatNotesPanel;
